import React, { useState, useEffect } from 'react';
import {
    BarChart3,
    Calendar,
    TrendingUp,
    Users,
    FlaskConical,
    Clock,
    Building2,
    Download,
    RefreshCw,
    FileText,
    FileSpreadsheet
} from 'lucide-react';
import { API_URL } from '../config/api';
import jspdf from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './StudyReports.css';

const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const StudyReports = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exportOpen, setExportOpen] = useState(false);
    const [activePreset, setActivePreset] = useState('month');
    const [dateRange, setDateRange] = useState(() => {
        const now = new Date();
        const startOfMonth = toYMD(new Date(now.getFullYear(), now.getMonth(), 1));
        const today = toYMD(now);
        return { start: startOfMonth, end: today };
    });

    const getPresetRange = (preset) => {
        const now = new Date();
        const today = toYMD(now);
        switch (preset) {
            case 'today': return { start: today, end: today };
            case 'week': { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return { start: toYMD(s), end: today }; }
            case 'month': return { start: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
            case 'quarter': { const q = Math.floor(now.getMonth() / 3) * 3; return { start: toYMD(new Date(now.getFullYear(), q, 1)), end: today }; }
            case 'year': return { start: toYMD(new Date(now.getFullYear(), 0, 1)), end: today };
            default: return { start: today, end: today };
        }
    };

    const handleExportPDF = () => {
        const doc = new jspdf('p', 'pt', 'a4');
        doc.setFontSize(18);
        doc.text("Reporte de Estadísticas de Estudios", 40, 40);
        doc.setFontSize(11);
        doc.text(`Periodo: ${dateRange.start} a ${dateRange.end}`, 40, 60);

        doc.text("Resumen General:", 40, 90);
        autoTable(doc, {
            startY: 100,
            head: [['Total', 'Completados', 'Pendientes', 'Cancelados']],
            body: [[
                stats.totals?.total || 0,
                stats.totals?.completed || 0,
                stats.totals?.pending || 0,
                stats.totals?.cancelled || 0
            ]],
            theme: 'grid'
        });

        doc.text("Estadísticas por Médico:", 40, doc.lastAutoTable.finalY + 30);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 40,
            head: [['Médico', 'Estudios']],
            body: stats.by_doctor?.map(d => [d.doctor_name, d.count]) || [],
            theme: 'striped'
        });

        doc.save(`Reporte_Estadisticas_Estudios_${dateRange.start}_${dateRange.end}.pdf`);
        setExportOpen(false);
    };

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();
        
        // Totals Sheet
        const totalsWs = XLSX.utils.json_to_sheet([{
            'Total': stats.totals?.total || 0,
            'Completados': stats.totals?.completed || 0,
            'Pendientes': stats.totals?.pending || 0,
            'Cancelados': stats.totals?.cancelled || 0
        }]);
        XLSX.utils.book_append_sheet(wb, totalsWs, "Resumen");

        // Doctors Sheet
        const docsWs = XLSX.utils.json_to_sheet(stats.by_doctor?.map(d => ({
            'Médico': d.doctor_name,
            'Cantidad': d.count
        })) || []);
        XLSX.utils.book_append_sheet(wb, docsWs, "Por Médico");

        XLSX.writeFile(wb, `Reporte_Estadisticas_Estudios_${dateRange.start}_${dateRange.end}.xlsx`);
        setExportOpen(false);
    };

    const handlePreset = (p) => {
        setActivePreset(p);
        setDateRange(getPresetRange(p));
    };

    const handleDateChange = (e) => {
        setActivePreset('');
        setDateRange(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    useEffect(() => {
        fetchStats();
    }, [dateRange]);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/reports/studies-stats?from_date=${dateRange.start}&to_date=${dateRange.end}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setStats(data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculatePercentage = (value, total) => {
        if (!total) return 0;
        return ((value / total) * 100).toFixed(1);
    };

    const formatHours = (hours) => {
        if (!hours) return '-';
        const h = parseFloat(hours);
        if (isNaN(h)) return '-';
        if (h < 24) return `${h.toFixed(1)} horas`;
        return `${(h / 24).toFixed(1)} días`;
    };

    return (
        <div className="study-reports reports-dashboard fade-in">
            {/* Header */}
            <div className="report-header-actions">
                <div className="report-title-wrapper">
                    <div className="report-title">
                        <BarChart3 size={28} />
                        <h2>Reportes de Estudios</h2>
                    </div>
                    <p>Estadísticas y análisis de productividad</p>
                </div>
                <div className="report-controls">
                    <div className="date-range-picker">
                        <Calendar size={16} />
                        <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} className="date-input" />
                        <span>a</span>
                        <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} className="date-input" />
                    </div>
                    <button className={`btn-icon ${loading ? 'spinning' : ''}`} onClick={fetchStats} title="Actualizar">
                        <RefreshCw size={18} />
                    </button>
                    <div className="export-menu-container" style={{ position: 'relative' }}>
                        <button className="btn-secondary" onClick={() => setExportOpen(!exportOpen)} title="Exportar">
                            <Download size={18} />
                            <span>Exportar</span>
                        </button>
                        {exportOpen && (
                            <div className="export-dropdown" style={{ 
                                position: 'absolute', top: '100%', right: 0, 
                                background: 'white', border: '1px solid #e2e8f0', 
                                borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 1000, minWidth: '160px', marginTop: '8px',
                                animation: 'dropdown-in 0.2s ease-out', overflow: 'hidden'
                            }}>
                                <button onClick={handleExportPDF} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileText size={16} color="#ef4444" />
                                    Exportar PDF
                                </button>
                                <button onClick={handleExportExcel} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    borderTop: '1px solid #f1f5f9', fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileSpreadsheet size={16} color="#10b981" />
                                    Exportar Excel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Presets */}
            <div className="date-presets" style={{ marginBottom: '1.5rem' }}>
                {[
                    { key: 'today', label: 'Hoy' },
                    { key: 'week', label: 'Semana' },
                    { key: 'month', label: 'Mes' },
                    { key: 'quarter', label: 'Trimestre' },
                    { key: 'year', label: 'Año' }
                ].map(p => (
                    <button
                        key={p.key}
                        className={`preset-chip ${activePreset === p.key ? 'active' : ''}`}
                        onClick={() => handlePreset(p.key)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="loading-state">Cargando estadísticas...</div>
            ) : stats ? (
                <>
                    {/* Summary Cards */}
                    <div className="stats-grid">
                        <div className="stat-card primary">
                            <div className="stat-icon">
                                <FlaskConical size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.totals?.total || 0}</span>
                                <span className="stat-label">Total Solicitados</span>
                            </div>
                        </div>

                        <div className="stat-card success">
                            <div className="stat-icon">
                                <TrendingUp size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.totals?.completed || 0}</span>
                                <span className="stat-label">Completados</span>
                                <span className="stat-percent">
                                    {calculatePercentage(stats.totals?.completed, stats.totals?.total)}%
                                </span>
                            </div>
                        </div>

                        <div className="stat-card warning">
                            <div className="stat-icon">
                                <Clock size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.totals?.pending || 0}</span>
                                <span className="stat-label">Pendientes</span>
                                <span className="stat-percent">
                                    {calculatePercentage(stats.totals?.pending, stats.totals?.total)}%
                                </span>
                            </div>
                        </div>

                        <div className="stat-card danger">
                            <div className="stat-icon">
                                <FlaskConical size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.totals?.cancelled || 0}</span>
                                <span className="stat-label">Cancelados</span>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="charts-grid">
                        {/* By Day Chart */}
                        <div className="chart-card">
                            <h3>Estudios por Día (últimos 7 días)</h3>
                            <div className="bar-chart">
                                {stats.by_day?.map((day, index) => {
                                    const maxCount = Math.max(...stats.by_day.map(d => d.count));
                                    const height = maxCount ? (day.count / maxCount) * 100 : 0;
                                    return (
                                        <div key={index} className="bar-item">
                                            <div
                                                className="bar"
                                                style={{ height: `${height}%` }}
                                                title={`${day.count} estudios`}
                                            >
                                                <span className="bar-value">{day.count}</span>
                                            </div>
                                            <span className="bar-label">
                                                {new Date(day.date).toLocaleDateString('es-MX', { weekday: 'short' })}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* By Category */}
                        <div className="chart-card">
                            <h3>Por Categoría</h3>
                            <div className="category-list">
                                {stats.by_category?.map((cat, index) => {
                                    const total = stats.by_category.reduce((sum, c) => sum + c.count, 0);
                                    const percentage = total ? ((cat.count / total) * 100).toFixed(1) : 0;
                                    return (
                                        <div key={index} className="category-item">
                                            <div className="category-info">
                                                <span className="category-name">{cat.category || 'Sin categoría'}</span>
                                                <span className="category-count">{cat.count}</span>
                                            </div>
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                            <span className="category-percent">{percentage}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* By Doctor */}
                        <div className="chart-card">
                            <h3>
                                <Users size={18} />
                                Por Médico (Top 10)
                            </h3>
                            <div className="doctor-list">
                                {stats.by_doctor?.map((doc, index) => (
                                    <div key={index} className="doctor-item">
                                        <span className="doctor-rank">#{index + 1}</span>
                                        <span className="doctor-name">{doc.doctor_name}</span>
                                        <span className="doctor-count">{doc.count} estudios</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* External Labs */}
                        <div className="chart-card">
                            <h3>
                                <Building2 size={18} />
                                Laboratorios Externos
                            </h3>
                            <div className="lab-list">
                                {stats.external_labs?.length > 0 ? (
                                    stats.external_labs.map((lab, index) => (
                                        <div key={index} className="lab-item">
                                            <span className="lab-name">{lab.laboratory_name}</span>
                                            <span className="lab-count">{lab.count} estudios</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-data">No hay estudios enviados a laboratorios externos</p>
                                )}
                            </div>
                        </div>

                        {/* Average Delivery Time */}
                        <div className="chart-card time-card">
                            <h3>
                                <Clock size={18} />
                                Tiempos Promedio de Entrega
                            </h3>
                            <div className="time-stats">
                                <div className="time-item">
                                    <span className="time-label">General</span>
                                    <span className="time-value">
                                        {formatHours(stats.avg_delivery_time?.avg_hours_all)}
                                    </span>
                                </div>
                                <div className="time-item internal">
                                    <span className="time-label">Interno</span>
                                    <span className="time-value">
                                        {formatHours(stats.avg_delivery_time?.avg_hours_internal)}
                                    </span>
                                </div>
                                <div className="time-item external">
                                    <span className="time-label">Externo</span>
                                    <span className="time-value">
                                        {formatHours(stats.avg_delivery_time?.avg_hours_external)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                </>
            ) : (
                <div className="empty-state">No hay datos disponibles</div>
            )}
        </div>
    );
};

export default StudyReports;
